import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContentForUnit } from '@/features/_data/useContentForUnits';
import { envIsElectron } from 'src/const/envCheck';
import { useUnitData } from '@/features/_data/useUnitData';
import { useOfflineAvailability } from '@/features/_data/useOfflineAvailability';

/*
  This hook is used to automatically start the player when the app is opened.
*/
export const useAutoStartPlayer = async (
  unitId: string | undefined,
  isOnline: boolean,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  const hasLaunchedRef = useRef<string | null>(null);

  const { data: playerContent, error: playerContentError } =
    useContentForUnit(unitId);
  const { data: unitData } = useUnitData(unitId);
  const { checkOfflineAvailability } = useOfflineAvailability();
  let isReadyForOffline = false;
  if (unitData) {
    isReadyForOffline = await checkOfflineAvailability(unitData);
  }

  useEffect(() => {
    if (!unitId) return;

    if (!unitData) return;

    if (hasLaunchedRef.current === unitId) return;

    const isPlayerPage = location.pathname.includes('/player');

    if (isPlayerPage) return;

    const hasValidConnection = isReadyForOffline || isOnline;

    if (!hasValidConnection) return;

    const hasValidContent = Boolean(playerContent && !playerContentError);

    if (!hasValidContent) return;

    const canStartElectronPlayer = Boolean(envIsElectron);

    if (!canStartElectronPlayer) return;

    hasLaunchedRef.current = unitId;
    window.electron.ipcRenderer.sendMessage('setupPlayer', {
      UNIT_ID: unitId,
      playerContent,
    });
    navigate(`/player/${unitId}`);
  }, [
    isOnline,
    unitId,
    playerContent,
    playerContentError,
    location.pathname,
    navigate,
  ]);
};
